/**
 * Unified inbox endpoint — multi-channel message feed (email + chat + PEC).
 *
 * BE source of truth:
 *   - ``ando-agent/src/api/v1/endpoints/inbox.py`` (GET
 *     ``/api/v1/inbox/list``) — landed alongside the v18 mockup
 *     "Conversazioni" surface that replaced the AI-chat-history feed.
 *
 * Each item represents ONE inbound message normalised across providers:
 * Gmail / Outlook for email, Slack / Teams / Google Chat for chat, Aruba
 * PEC for italian certified mail. The hub renders rows from this single
 * list; provider-specific drawer detail uses the existing
 * ``/documents/email/{message_id}`` + ``/documents/chat/{conversation_id}``
 * endpoints.
 *
 * v1 reference: none — v1 had per-provider hubs (EmailHubPage / SlackHub /
 * PECHub) with no unified feed. The cross-channel inbox is a v2 surface.
 */

import { apiClient } from "../ApiClient.js";

/** Channel enum — matches the BE ``InboxChannel`` literal union. */
export type InboxChannel = "email" | "slack" | "teams" | "google_chat" | "pec";

/** Provider id — granular routing key (e.g. ``gmail`` vs ``outlook``). */
export type InboxProviderId = "gmail" | "outlook" | "slack" | "teams" | "google_chat" | "aruba_pec";

/**
 * One row in the unified inbox feed. The shape mirrors the BE
 * serialiser — no client-side transforms beyond display formatting.
 */
export interface InboxItem {
  readonly message_id: string;
  readonly channel: InboxChannel;
  readonly provider_id: InboxProviderId;
  readonly sender_display: string;
  readonly subject: string;
  readonly preview: string;
  /** ISO UTC timestamp. */
  readonly received_at: string;
  readonly has_attachments: boolean;
  readonly is_unread: boolean;
  /**
   * Optional deep-link to the provider's native UI for the conversation.
   * Set by the live-feed adapter for CHAT rows (Slack permalink / Teams
   * web URL / Google Chat space link); empty for email rows. The detail
   * pane uses it as a graceful fallback CTA ("Apri in {provider}")
   * when the indexed chat-detail endpoint can't serve the body.
   */
  readonly web_link?: string;
  /**
   * BE provider slug for the live row (``gmail`` / ``outlook`` / ``pec_aruba``
   * / ``slack`` / ``teams`` / ``google_chat``). Differs from ``provider_id``
   * (FE slug) only for PEC (``pec_aruba`` vs ``aruba_pec``). Forwarded to
   * ``/inbox/{id}/thread`` as a query param so the BE can fall back to a
   * provider-live fetch when the message isn't ingested yet. Undefined for
   * legacy (non-live) ingested rows.
   */
  readonly live_provider?: InboxLiveProvider;
  /**
   * Mailbox the row belongs to (the user's own email or chat account).
   * Disambiguates multi-account users on the live-thread fallback so the
   * BE picks the right OAuth connection. Pairs with ``live_provider``.
   */
  readonly live_account?: string;
  /** Native chat space/channel id, distinct from the last message id. */
  readonly live_conversation_id?: string;
}

/** Per-channel unread counts — used by the sub-tab chip badges. */
export interface InboxChannelCounts {
  readonly email: number;
  readonly slack: number;
  readonly teams: number;
  readonly google_chat: number;
  readonly pec: number;
}

export interface InboxListResponse {
  readonly items: readonly InboxItem[];
  readonly next_cursor: string | null;
  /** Per-channel TOTAL counts over the 30-day inbox window. */
  readonly counts: InboxChannelCounts;
  /** Per-channel UNREAD counts (``is_read = FALSE``) — drives the
   *  "N non lette" hero badge. Added June 1, 2026 (BE migration
   *  ``v2_20260601_is_read``). */
  readonly counts_unread: InboxChannelCounts;
}

export interface InboxListFilters {
  /**
   * Restrict to a subset of channels. When omitted the BE returns every
   * channel the caller has connectors for (cross-channel default).
   */
  readonly channels?: ReadonlyArray<InboxChannel>;
  /** When ``true`` the BE filters to ``is_unread=true`` rows only. */
  readonly unreadOnly?: boolean;
  /** Page size — BE default is 50, hard-capped at 200. */
  readonly limit?: number;
  /** Opaque cursor from the previous response's ``next_cursor``. */
  readonly cursor?: string;
}

/**
 * GET ``/inbox/list?channels=email,slack&unread_only=true&limit=50``.
 *
 * The response is paginated: when ``next_cursor`` is non-null, pass it as
 * ``cursor`` on the next call to fetch the following page. Counts in the
 * response are GLOBAL (not page-scoped) so the FE can render chip badges
 * without summing across pages.
 */
export async function listInbox(filters: InboxListFilters = {}): Promise<InboxListResponse> {
  const params = new URLSearchParams();
  if (filters.channels && filters.channels.length > 0) {
    params.set("channels", filters.channels.join(","));
  }
  if (filters.unreadOnly) params.set("unread_only", "true");
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.cursor) params.set("cursor", filters.cursor);

  const qs = params.toString();
  const path = qs ? `/inbox/list?${qs}` : "/inbox/list";
  return await apiClient.get<InboxListResponse>(path);
}

// ---------------------------------------------------------------------------
// GET /inbox/live — provider-truth feed (May 2026).
//
// Bypasses the local DB index and pulls messages directly from each
// connected provider (Gmail / Outlook / Aruba PEC) in real time. The
// provider owns ``is_read`` state — Ando just enriches each row with an
// ``is_ingested`` flag indicating whether the message is already part of
// our RAG index.
//
// Distinct from ``/inbox/list``: that endpoint serves the indexed copy
// (used for cross-channel chat threads that aren't part of the live
// pipeline yet). The Conversazioni hub defaults to ``/live``; the indexed
// feed is kept as a sibling source for chat-only sub-tabs.
//
// IMPORTANT: do NOT call ``POST /inbox/mark-read`` against rows from this
// endpoint — provider state is canonical, any FE-driven flip would be
// overwritten on the next refresh.
// ---------------------------------------------------------------------------

/**
 * Providers emitted by the live endpoint. May 31, 2026 — widened to
 * include chat providers (``slack`` / ``teams`` / ``google_chat``) now
 * that the BE has folded them into the live pipeline alongside the
 * email + PEC ones. Each row carries a ``kind`` discriminator so
 * consumers can route email-shaped vs chat-shaped rendering.
 */
export type InboxLiveProvider =
  | "gmail"
  | "outlook"
  | "pec_aruba"
  | "slack"
  | "teams"
  | "google_chat";

/** Row kind discriminator — ``email`` for mail/PEC, ``chat`` for IM threads. */
export type InboxLiveKind = "email" | "chat";

/** Conversation shape (chat rows only — ``""`` for email). */
export type InboxLiveConversationType =
  | "channel"
  | "dm"
  | "group_chat"
  | "space"
  | "";

/**
 * One row from ``GET /inbox/live``. Mirrors the BE serialiser one-to-one.
 *
 * ``message_id`` is the NATIVE provider id (Gmail msg id / Outlook id /
 * PEC native id / chat message id) — no opaque Ando proxy, so downstream
 * detail/thread endpoints (``/documents/email/{id}``,
 * ``/inbox/{id}/thread``, ``/documents/chat/{id}``) can take it directly.
 *
 * May 31, 2026 — extended with 5 chat-specific fields (default empty
 * for email rows) so the unified live feed can carry both kinds.
 */
export interface InboxLiveItem {
  readonly message_id: string;
  readonly thread_id: string;
  readonly provider: InboxLiveProvider;
  /** Mailbox the message was fetched from — the user's own account. */
  readonly account: string;
  readonly subject: string;
  /** Friendly + address combined, e.g. ``Mario Rossi <mario@x.it>``. */
  readonly sender_display: string;
  readonly sender_email: string;
  readonly snippet: string;
  /** ISO 8601 UTC. */
  readonly received_at: string;
  /** Provider-truth read state (Gmail UNREAD label / Outlook isRead). */
  readonly is_read: boolean;
  readonly is_starred: boolean;
  readonly has_attachments: boolean;
  readonly labels: ReadonlyArray<string>;
  /** ``false`` when the message hasn't landed in our RAG index yet. */
  readonly is_ingested: boolean;
  /** Email vs chat row (drives renderer + detail-pane routing). */
  readonly kind: InboxLiveKind;
  /** Chat: native conversation id (Slack channel / Teams chat / Chat space). Email: ``""``. */
  readonly conversation_id: string;
  /** Chat: channel / dm / group_chat / space. Email: ``""``. */
  readonly conversation_type: InboxLiveConversationType;
  /** Chat: provider-supplied unread count for the conversation. Email: ``0``. */
  readonly unread_count: number;
  /** Chat: deep-link into the provider's native UI. Email: ``""``. */
  readonly web_link: string;
}

/**
 * Per-account diagnostic — surfaces silent provider failures (token
 * expired, rate-limited) without breaking the merged feed. The aggregate
 * ``items`` array still contains rows from accounts that succeeded.
 */
export interface InboxLivePerAccount {
  readonly account: string;
  readonly provider: InboxLiveProvider;
  readonly ok: boolean;
  readonly fetched: number;
  readonly error: string | null;
}

export interface InboxLiveResponse {
  readonly items: ReadonlyArray<InboxLiveItem>;
  /** Opaque — round-trip verbatim on the next call. ``null`` at end. */
  readonly next_cursor: string | null;
  readonly per_account: ReadonlyArray<InboxLivePerAccount>;
}

export interface InboxLiveFilters {
  /** BE default 100, hard cap 200. */
  readonly limit?: number;
  readonly cursor?: string;
  /**
   * Smart-view shortcut (Jun 24, 2026) — pushed down to the provider's
   * native query (Gmail ``q=is:unread``, Graph ``$filter=isRead eq false``).
   * Supported: ``unread`` | ``starred`` | ``attachments``. The FE-only
   * views (``tutti`` / ``today`` / ``needs_reply``) have no native
   * equivalent and remain post-filters on the client — passing them
   * here is a no-op (BE ignores).
   */
  readonly view?: string;
  /**
   * Email direction (Jul 2026). ``"sent"`` fetches the provider's SENT folder
   * (Gmail ``SENT`` / Outlook ``sentitems``) instead of the INBOX — the
   * "Inviate" view. Omit (or ``"received"``) for the INBOX. Chat channels are
   * excluded when ``"sent"``.
   */
  readonly direction?: "sent" | "received";
  /**
   * Free-text search pushed to the provider (Gmail ``q=`` / Outlook
   * ``$search``). Without this, search would client-filter over unbounded
   * INBOX pages and the infinite scroll would hunt the whole history.
   */
  readonly q?: string;
}

/**
 * GET ``/inbox/live?limit=100&cursor=<opaque>&view=<smart>``.
 *
 * Always-fresh: the BE forwards each request to the user's mail providers
 * synchronously. Pagination is keyset-style; ``next_cursor`` is opaque
 * and must be echoed verbatim.
 */
export async function listInboxLive(filters: InboxLiveFilters = {}): Promise<InboxLiveResponse> {
  const params = new URLSearchParams();
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.cursor) params.set("cursor", filters.cursor);
  if (filters.view) params.set("view", filters.view);
  if (filters.direction) params.set("direction", filters.direction);
  if (filters.q && filters.q.trim()) params.set("q", filters.q.trim());

  const qs = params.toString();
  const path = qs ? `/inbox/live?${qs}` : "/inbox/live";
  return await apiClient.get<InboxLiveResponse>(path);
}

// ---------------------------------------------------------------------------
// POST /inbox/mark-read — legacy cache-only flip (admin / bulk tooling).
// FE callers SHOULD use ``markInboxReadInProvider`` below — it forwards the
// flag to the upstream provider FIRST so a subsequent ``/inbox/live`` refresh
// no longer rolls back the change.
// ---------------------------------------------------------------------------

export interface MarkInboxReadResponse {
  readonly updated: boolean;
}

export async function markInboxRead(
  messageId: string,
  isRead = true,
): Promise<MarkInboxReadResponse> {
  if (!messageId) throw new Error("markInboxRead: messageId required");
  return await apiClient.post<MarkInboxReadResponse>("/inbox/mark-read", {
    message_id: messageId,
    is_read: isRead,
  });
}

// ---------------------------------------------------------------------------
// POST /inbox/messages/{message_id}/mark-read — provider-first mark-as-read.
//
// June 11, 2026: forwards the flag to Gmail / Outlook / Slack / Teams /
// Google Chat (PEC opts out cleanly via ``provider_unsupported``) BEFORE
// mirroring ``content_items.is_read`` locally, so the next ``/inbox/live``
// refresh agrees with the user's intent rather than rolling it back.
//
// The FE auto-fires this when the user opens a row in the Conversazioni
// drawer (debounced). Optimistic-update the TanStack cache + rollback on
// ``success === false``.
// ---------------------------------------------------------------------------

/**
 * Inline reconnect hint embedded in the response when the upstream
 * provider rejected the call for a missing OAuth scope (typical: token
 * issued before ``gmail.modify`` / ``Mail.ReadWrite`` was required).
 *
 * Mirrors the canonical ``CapabilityUpgradePath`` shape so the FE can
 * reuse the same primitive that consumes ``GET /api/v1/capabilities``.
 * ``null`` in every other outcome.
 */
export interface MarkInboxReadScopeUpgrade {
  /** Canonical capability slug (today: ``email_mark_read``). */
  readonly capability: string;
  /** Provider this hint targets (``gmail`` / ``outlook``). */
  readonly provider: string;
  /** Relative URL the FE opens to start the OAuth flow. */
  readonly consent_url: string;
  /** Italian button label, e.g. ``Riconnetti Gmail``. */
  readonly human_action_it: string;
  /** Scopes the new OAuth flow will request on top of the existing ones. */
  readonly missing_scopes: readonly string[];
}

export interface MarkInboxReadInProviderResponse {
  /**
   * ``true`` when the upstream provider acknowledged the flip OR
   * signalled that it has no per-user read concept (PEC). ``false`` for
   * provider 401 / timeout / circuit-open — the FE rolls back its
   * optimistic write in that case.
   */
  readonly success: boolean;
  /**
   * ``true`` when the provider has no per-user read flag (PEC certified
   * mail). The local cache is still flipped so the chip counter
   * reflects the user's intent.
   */
  readonly provider_unsupported: boolean;
  /**
   * ``true`` when ``content_items.is_read`` was updated locally. ``false``
   * when the provider call failed (we keep the cache aligned with the
   * upstream truth on failure).
   */
  readonly cached: boolean;
  /** Provider-specific error slug; ``null`` on success. */
  readonly error: string | null;
  /**
   * Set ONLY on a scope-related provider rejection. Renders a contextual
   * "Riconnetti Gmail / Outlook" banner instead of a generic error
   * toast. See :class:`MarkInboxReadScopeUpgrade`.
   */
  readonly scope_upgrade?: MarkInboxReadScopeUpgrade | null;
}

export interface MarkInboxReadInProviderParams {
  /** BE provider slug from ``InboxLiveItem.provider``. */
  readonly provider: InboxLiveProvider;
  /** Mailbox / workspace key the row belongs to — disambiguates multi-account. */
  readonly account?: string;
  /** Required for chat rows (Slack channel id / Teams chat id / Google Chat space). */
  readonly conversationId?: string;
  /** Defaults to ``true``. ``false`` is best-effort (some chat providers can't undo). */
  readonly isRead?: boolean;
}

/**
 * POST ``/inbox/messages/{message_id}/mark-read`` — provider-truth flip.
 *
 * Pass the matching fields from the ``InboxLiveItem`` the FE is already
 * holding so the BE doesn't have to re-resolve them. The response is
 * intentionally a 200 with ``success=false`` on provider errors — never a
 * 5xx — so the optimistic-update rollback path stays simple.
 */
export async function markInboxReadInProvider(
  messageId: string,
  params: MarkInboxReadInProviderParams,
): Promise<MarkInboxReadInProviderResponse> {
  if (!messageId) throw new Error("markInboxReadInProvider: messageId required");
  if (!params.provider) throw new Error("markInboxReadInProvider: provider required");
  return await apiClient.post<MarkInboxReadInProviderResponse>(
    `/inbox/messages/${encodeURIComponent(messageId)}/mark-read`,
    {
      provider: params.provider,
      account: params.account ?? null,
      conversation_id: params.conversationId ?? null,
      is_read: params.isRead ?? true,
    },
  );
}

// ---------------------------------------------------------------------------
// Detail endpoints — used by the drawer detail pane when a row is opened.
// Email + chat detail BE handlers already exist (legacy single-channel hubs
// in v1 consumed them); the inbox drawer reuses them rather than introducing
// a unified detail endpoint.
// ---------------------------------------------------------------------------

export interface InboxEmailAttachmentInfo {
  readonly attachment_id: string;
  readonly filename: string;
  readonly mime_type: string | null;
  readonly size_bytes: number | null;
}

/**
 * Wire shape for ``GET /documents/email/{message_id}``. The BE returns the
 * full message body as plain text or sanitised HTML (``body_html`` only set
 * when the source provider sent multipart/alternative).
 */
export interface InboxEmailDetail {
  readonly message_id: string;
  readonly subject: string;
  readonly sender_display: string;
  readonly sender_address: string;
  readonly recipients: ReadonlyArray<string>;
  readonly cc: ReadonlyArray<string>;
  readonly received_at: string;
  readonly body_text: string | null;
  readonly body_html: string | null;
  readonly attachments: ReadonlyArray<InboxEmailAttachmentInfo>;
}

export async function getInboxEmailDetail(messageId: string): Promise<InboxEmailDetail> {
  if (!messageId) throw new Error("getInboxEmailDetail: messageId required");
  return await apiClient.get<InboxEmailDetail>(`/documents/email/${encodeURIComponent(messageId)}`);
}

// ---------------------------------------------------------------------------
// Thread endpoint (May 2026) — full chronological conversation view.
//
// Powers the Gmail/Outlook-style email thread renderer inside the inbox
// drawer. The BE resolves ``message_id`` → ``thread_id`` and returns every
// sibling message ordered oldest-first. ACL is re-checked per-row by the BE.
// ---------------------------------------------------------------------------

export type EmailThreadDirection = "sent" | "received";

/**
 * Per-message attachment descriptor (June 13, 2026 — single-fetch contract).
 *
 * Populated by the BE thread endpoint so the FE can render an in-context,
 * click-to-download list under every message that has attachments — no
 * second round-trip required. The legacy
 * ``GET /inbox/attachments/{message_id}`` + ``InboxAttachmentLink`` pair
 * stays available for thread payloads that pre-date the schema change.
 *
 * ``attachment_id`` is the provider-native id (Gmail opaque token, Outlook
 * GUID). The download / preview URLs below build their final URL from
 * ``(message_id, attachment_id, provider, account)`` — the FE never has
 * to know which provider it's talking to.
 *
 * ``is_inline`` flags an attachment referenced by ``<img src="cid:…">`` in
 * the body — the body already renders it, so the visible list can hide
 * inline attachments without losing user-facing files.
 */
export interface EmailAttachmentDescriptor {
  readonly attachment_id: string;
  readonly filename: string;
  readonly mime_type: string | null;
  readonly size_bytes: number | null;
  readonly is_inline: boolean;
  readonly content_id: string | null;
}

export interface EmailThreadMessage {
  readonly message_id: string;
  readonly subject: string;
  readonly sender_display: string;
  readonly sender_address: string;
  readonly recipients: ReadonlyArray<string>;
  readonly cc: ReadonlyArray<string>;
  /** ISO-8601 UTC, chronological sort key. May be ``""`` for malformed rows. */
  readonly sent_at: string;
  readonly body_text: string | null;
  readonly body_html: string | null;
  readonly has_attachments: boolean;
  readonly attachment_count: number;
  /**
   * Drawer-ready attachment list. Empty for legacy thread payloads where
   * the BE only knows the count (Weaviate-cached path); callers may fall
   * back to the legacy ``GET /inbox/attachments/{message_id}`` endpoint
   * in that case.
   */
  readonly attachments: ReadonlyArray<EmailAttachmentDescriptor>;
  readonly is_unread: boolean;
  readonly direction: EmailThreadDirection;
}

export interface EmailThreadResponse {
  readonly thread_id: string;
  /** Canonical subject — taken from the first message in the thread. */
  readonly subject: string;
  readonly message_count: number;
  readonly unread_count: number;
  /** Oldest → newest. */
  readonly messages: ReadonlyArray<EmailThreadMessage>;
}

export interface InboxEmailThreadParams {
  /** BE provider slug from the live row (``gmail`` / ``outlook`` /
   *  ``pec_aruba``). When provided together with ``account`` the BE will
   *  fall back to a provider-live fetch on Weaviate misses. */
  readonly provider?: string;
  /** Mailbox the row belongs to. Required when ``provider`` is set. */
  readonly account?: string;
  /** Read the current provider thread, including replies not yet indexed. */
  readonly live?: boolean;
}

export interface DraftEmailReplyRequest {
  /** BE provider slug from the live row (``gmail`` / ``outlook`` /
   *  ``pec_aruba``). When provided together with ``account`` the BE
   *  will use the provider-live thread for context — same DRY path as
   *  ``getInboxEmailThread``. */
  readonly provider?: string;
  readonly account?: string;
  /** Optional steering nudge (tone, length…); ``""`` for the bare
   *  one-click flow. */
  readonly instructions?: string;
}

export interface DraftEmailReplyResponse {
  readonly body: string;
  readonly subject: string;
}

/**
 * POST ``/inbox/{message_id}/draft-reply`` — LLM-author a reply AS the
 * authenticated user. The BE pins user identity from the JWT so the
 * model never impersonates the original sender (June 1, 2026 contract).
 */
export async function draftEmailReply(
  messageId: string,
  payload: DraftEmailReplyRequest = {},
): Promise<DraftEmailReplyResponse> {
  if (!messageId) throw new Error("draftEmailReply: messageId required");
  return await apiClient.post<DraftEmailReplyResponse>(
    `/inbox/${encodeURIComponent(messageId)}/draft-reply`,
    {
      provider: payload.provider ?? null,
      account: payload.account ?? null,
      instructions: payload.instructions ?? "",
    },
  );
}

export async function getInboxEmailThread(
  messageId: string,
  params: InboxEmailThreadParams = {},
): Promise<EmailThreadResponse> {
  if (!messageId) throw new Error("getInboxEmailThread: messageId required");
  const search = new URLSearchParams();
  if (params.provider) search.set("provider", params.provider);
  if (params.account) search.set("account", params.account);
  if (params.live) search.set("live", "true");
  const qs = search.toString();
  return await apiClient.get<EmailThreadResponse>(
    `/inbox/${encodeURIComponent(messageId)}/thread${qs ? `?${qs}` : ""}`,
  );
}

/**
 * Wire shape for ``GET /documents/chat/{conversation_id}`` — Slack / Teams
 * / Google Chat thread snapshot. ``messages`` is ordered oldest-first.
 */
export interface InboxChatMessageEntry {
  readonly message_name?: string | null;
  readonly can_edit?: boolean;
  readonly author_display: string;
  readonly body_text: string;
  readonly posted_at: string;
}

export interface InboxChatDetail {
  readonly participant_names?: ReadonlyArray<string>;
  readonly conversation_id: string;
  readonly channel_name: string;
  readonly messages: ReadonlyArray<InboxChatMessageEntry>;
}

export interface InboxChatDetailParams {
  /** BE provider slug from the live row (``slack`` / ``teams`` /
   *  ``google_chat``). When provided together with ``account`` the BE
   *  will fall back to a provider-live fetch on Weaviate misses —
   *  mirrors the email thread live-fallback. */
  readonly provider?: string;
  /** Workspace / account the conversation belongs to. Required when
   *  ``provider`` is set. */
  readonly account?: string;
}

export async function getInboxChatDetail(
  conversationId: string,
  params: InboxChatDetailParams = {},
): Promise<InboxChatDetail> {
  if (!conversationId) throw new Error("getInboxChatDetail: conversationId required");
  const search = new URLSearchParams();
  if (params.provider) search.set("provider", params.provider);
  if (params.account) search.set("account", params.account);
  const qs = search.toString();
  return await apiClient.get<InboxChatDetail>(
    `/documents/chat/${encodeURIComponent(conversationId)}${qs ? `?${qs}` : ""}`,
  );
}

/**
 * Wire shape for ``GET /attachments/{message_id}`` — presigned MinIO URLs
 * for inline attachment download. The BE keeps presign TTL at 5 min.
 */
export interface InboxAttachmentLink {
  readonly attachment_id: string;
  readonly filename: string;
  readonly mime_type: string | null;
  readonly size_bytes: number | null;
  readonly presigned_url: string;
}

export async function getInboxAttachments(
  messageId: string,
): Promise<ReadonlyArray<InboxAttachmentLink>> {
  if (!messageId) throw new Error("getInboxAttachments: messageId required");
  const resp = await apiClient.get<{
    attachments: ReadonlyArray<InboxAttachmentLink>;
  }>(`/attachments/${encodeURIComponent(messageId)}`);
  return resp.attachments;
}

// ---------------------------------------------------------------------------
// Per-message attachment URLs (June 13, 2026 — REST-proper, provider-agnostic).
//
// The download / preview routes resolve the right OAuth connection on the
// BE via ``provider`` + ``account`` and delegate to
// ``EmailConnectorBase.download_attachment`` — Gmail opaque ids, Outlook
// GUIDs and any future provider go through the same path. The FE only
// needs the message_id + attachment_id from
// :class:`EmailAttachmentDescriptor`.
// ---------------------------------------------------------------------------

export interface EmailAttachmentEndpointParams {
  /** Provider slug from the inbox row (``gmail`` / ``outlook`` / …). */
  readonly provider: string;
  /** Mailbox the message belongs to (the user's own address). */
  readonly account: string;
}

function _buildAttachmentUrl(
  kind: "download" | "preview",
  messageId: string,
  attachmentId: string,
  params: EmailAttachmentEndpointParams,
): string {
  const qs = new URLSearchParams({
    provider: params.provider,
    account: params.account,
  }).toString();
  return (
    `/inbox/messages/${encodeURIComponent(messageId)}` +
    `/attachments/${encodeURIComponent(attachmentId)}/${kind}?${qs}`
  );
}

/** Relative path for the BE download route — for ``<a href>`` rendering
 *  prefer :func:`emailAttachmentDownloadAbsoluteUrl`. */
export function emailAttachmentDownloadPath(
  messageId: string,
  attachmentId: string,
  params: EmailAttachmentEndpointParams,
): string {
  return _buildAttachmentUrl("download", messageId, attachmentId, params);
}

/** Relative path for the BE preview route. */
export function emailAttachmentPreviewPath(
  messageId: string,
  attachmentId: string,
  params: EmailAttachmentEndpointParams,
): string {
  return _buildAttachmentUrl("preview", messageId, attachmentId, params);
}

/**
 * Fetch the attachment body via authenticated XHR and trigger a browser
 * download. We deliberately don't expose a raw ``<a href>`` to the BE
 * route: the API client carries the bearer token in a header, not a
 * cookie, so a naked link would 401. The blob roundtrip is the same
 * pattern ``chat-file-attachments.ts`` uses.
 */
export async function downloadEmailAttachment(
  messageId: string,
  descriptor: Pick<EmailAttachmentDescriptor, "attachment_id" | "filename">,
  params: EmailAttachmentEndpointParams,
): Promise<void> {
  if (!messageId) throw new Error("downloadEmailAttachment: messageId required");
  const path = emailAttachmentDownloadPath(messageId, descriptor.attachment_id, params);
  const blob = await apiClient.requestBlob(path, { method: "GET" });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = descriptor.filename || "attachment";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Defer revocation so Safari has time to read the blob URL on click.
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  }
}

/**
 * Open the attachment preview in a new tab. The BE serves the content
 * with ``Content-Disposition: inline`` so the browser renders it in its
 * native viewer (PDF, image, text). Falls back to a download if the
 * server can't preview (e.g. arbitrary binary).
 */
export async function previewEmailAttachment(
  messageId: string,
  descriptor: Pick<EmailAttachmentDescriptor, "attachment_id" | "filename">,
  params: EmailAttachmentEndpointParams,
): Promise<void> {
  if (!messageId) throw new Error("previewEmailAttachment: messageId required");
  const path = emailAttachmentPreviewPath(messageId, descriptor.attachment_id, params);
  const blob = await apiClient.requestBlob(path, { method: "GET" });
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (!opened) {
    // Popup blocked — degrade to download so the user still gets the file.
    return downloadEmailAttachment(messageId, descriptor, params);
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ---------------------------------------------------------------------------
// AI proposal (May 2026) — contextual one-shot suggestion per message.
//
// BE source of truth:
//   - ``ando-agent/src/api/v1/endpoints/inbox_ai.py`` (GET
//     ``/api/v1/inbox/{message_id}/ai-proposal``)
//   - ``ando-agent/src/services/inbox/ai_proposal_service.py``
//
// The BE returns a *single* proposal envelope with up to two CTAs. When
// ``kind === "none"`` the FE must hide the card silently (the BE never
// raises for "no useful suggestion"). The ``payload`` object on each CTA
// matches the corresponding HITL ``ActionPayload`` subclass — POSTing it
// to ``/actions/{action_type}/propose`` opens the editable HITL card.
// ---------------------------------------------------------------------------

export type AiProposalKind = "register_invoice" | "create_task" | "forward" | "reply" | "none";

export interface AiProposalCta {
  /** IT button label, e.g. ``Registra in FiC``. */
  readonly label: string;
  /** Routes the HITL POST (maps to ``ActionPayload.kind``). */
  readonly action_type: string;
  /** Pre-filled fields for the editable HITL card. */
  readonly payload: Readonly<Record<string, unknown>>;
}

export interface AiProposalResponse {
  readonly kind: AiProposalKind;
  /** Italian one-liner (≤280 chars). Empty when ``kind === "none"``. */
  readonly summary: string;
  readonly cta_primary: AiProposalCta | null;
  readonly cta_secondary: AiProposalCta | null;
}

/**
 * GET ``/inbox/{message_id}/ai-proposal`` — never rejects for "no proposal".
 *
 * The BE memoises the LLM call in Redis with a 1h TTL keyed on
 * ``(user_id, message_id)``; the FE hook below mirrors that with a
 * matching ``staleTime`` so the React Query cache stays in lockstep.
 */
export async function getInboxAiProposal(messageId: string): Promise<AiProposalResponse> {
  if (!messageId) throw new Error("getInboxAiProposal: messageId required");
  return await apiClient.get<AiProposalResponse>(
    `/inbox/${encodeURIComponent(messageId)}/ai-proposal`,
  );
}

// ---------------------------------------------------------------------------
// AI summary (May 2026) — per-message TL;DR card with up-to-4 action chips.
//
// Companion to the AI proposal endpoint: where ``ai-proposal`` returns ONE
// structured HITL action, ``summary`` returns a read-only Italian one-liner
// + lightweight action chips (open url, archive, snooze, reply prefilled,
// extract to task). Pinned ABOVE the body HTML in the v2 SPA email viewer.
//
// BE source of truth:
//   - ``ando-agent/src/api/v1/endpoints/inbox_summary.py`` (GET
//     ``/api/v1/inbox/{message_id}/summary``)
//   - ``ando-agent/src/services/inbox/email_summary_service.py``
//
// The BE returns **204 No Content** when no useful summary applies (body
// < 500 chars, calendar invite, LLM declined). The wrapper below
// translates that to ``null`` so callers can collapse the card silently.
// ---------------------------------------------------------------------------

export type EmailSummaryActionIntent =
  | "open_url"
  | "archive"
  | "snooze"
  | "reply_prefilled"
  | "extract_to_task";

export interface EmailSummaryAction {
  readonly label: string;
  readonly intent: EmailSummaryActionIntent;
  /** Intent-specific parameters — shapes documented in the BE service. */
  readonly payload: Readonly<Record<string, unknown>>;
  /** Optional lucide-react icon name hint. */
  readonly icon: string | null;
}

export interface EmailSummaryResponse {
  readonly message_id: string;
  /** Italian TL;DR, 1-3 short lines (≤400 chars). */
  readonly summary: string;
  readonly actions: ReadonlyArray<EmailSummaryAction>;
  /** True when the BE served the envelope from its Redis cache. */
  readonly cached: boolean;
  /** ISO-8601 UTC timestamp of LLM generation. */
  readonly generated_at: string;
}

/**
 * GET ``/inbox/{message_id}/summary`` — resolves to ``null`` when the BE
 * returns 204 (no useful summary).
 *
 * ApiClient deserialises a 204 body as ``{}`` (see ``parseSuccessBody`` in
 * ``ApiClient.ts``), so we sniff the missing required fields rather than
 * relying on the status code being surfaced.
 */
export async function getInboxEmailSummary(
  messageId: string,
): Promise<EmailSummaryResponse | null> {
  if (!messageId) throw new Error("getInboxEmailSummary: messageId required");
  const raw = await apiClient.get<EmailSummaryResponse | Record<string, never>>(
    `/inbox/${encodeURIComponent(messageId)}/summary`,
  );
  // 204 → ApiClient returns `{}`. Treat any envelope without a populated
  // ``summary`` field as "no useful summary" so callers collapse silently.
  if (!raw || typeof (raw as EmailSummaryResponse).summary !== "string") {
    return null;
  }
  return raw as EmailSummaryResponse;
}

/** Provider-authorized edit of an existing own Google Chat message. */
export async function editGoogleChatMessage(input: {
  readonly account: string;
  readonly message_name: string;
  readonly text: string;
}): Promise<{ readonly message_name: string; readonly text: string }> {
  return await apiClient.patch("/inbox/google-chat/messages", input);
}
